import { useMemo } from "react";
import { motion } from "framer-motion";

const dayNames = ["M", "T", "W", "T", "F"];
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const bookedSlots = [2, 7, 11, 14, 18];
const timeSlots = [
  { time: "9:00 AM", name: "Dr. Smith", delay: 0 },
  { time: "2:30 PM", name: "Team Sync", delay: 3 },
  { time: "11:00 AM", name: "Consultation", delay: 6 },
];

function getWeekdayDates() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const dates: number[] = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month, d).getDay();
    if (dow >= 1 && dow <= 5) dates.push(d);
  }
  // Show up to 20 weekdays (4 rows of 5)
  return { dates: dates.slice(0, 20), month: monthNames[month], year };
}

export const CalendarGridBg = () => {
  const { dates, month, year } = useMemo(getWeekdayDates, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <style>{`
        @keyframes calCell {
          0%, 100% { background-color: transparent; }
          12%, 35% { background-color: hsl(239 84% 67% / 0.3); }
        }
        @keyframes confirmPulse {
          0%, 100% { opacity: 0; transform: translateX(8px); }
          15%, 85% { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div className="absolute inset-0 flex flex-col p-4 gap-2">
        {/* Month header */}
        <div className="flex items-center justify-between px-1">
          <span className="text-[8px] font-semibold text-white/40">{month} {year}</span>
          <div className="flex gap-1">
            <div className="w-3 h-3 rounded-full border border-white/[0.15] flex items-center justify-center">
              <span className="text-[6px] text-white/30">&lt;</span>
            </div>
            <div className="w-3 h-3 rounded-full border border-white/[0.15] flex items-center justify-center">
              <span className="text-[6px] text-white/30">&gt;</span>
            </div>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-5 gap-[4px] px-1">
          {dayNames.map((d, i) => (
            <div key={i} className="text-center text-[6px] font-medium text-white/25">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-5 gap-[4px] flex-1">
          {dates.map((date, i) => {
            const isBooked = bookedSlots.includes(i);
            return (
              <div
                key={i}
                className="relative rounded-md border border-white/[0.1] flex items-center justify-center"
                style={{
                  animation: isBooked ? `calCell 12s ease-in-out ${i * 0.6}s infinite` : undefined,
                }}
              >
                <span className="text-[7px] font-mono text-white/35">{date}</span>
                {isBooked && (
                  <div className="absolute bottom-[2px] left-1/2 -translate-x-1/2 w-[4px] h-[4px] rounded-full"
                    style={{
                      backgroundColor: "hsl(150 60% 50% / 0.5)",
                      animation: `calCell 12s ease-in-out ${i * 0.6}s infinite`,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Upcoming bookings feed */}
        <div className="flex flex-col gap-[3px]">
          {timeSlots.map((slot, i) => (
            <motion.div
              key={i}
              className="flex items-center gap-[6px] rounded-md px-[6px] py-[3px] border border-white/[0.06]"
              style={{
                backgroundColor: "hsl(0 0% 100% / 0.03)",
                animation: `confirmPulse 9s ease-in-out ${slot.delay}s infinite`,
              }}
            >
              <div className="w-[3px] h-[10px] rounded-full bg-indigo-400/50 shrink-0" />
              <span className="text-[6px] font-mono text-indigo-300/60 shrink-0">{slot.time}</span>
              <span className="text-[6px] text-white/30 truncate">{slot.name}</span>
              <motion.div
                className="ml-auto w-[6px] h-[6px] rounded-full shrink-0"
                style={{ backgroundColor: "hsl(150 60% 50% / 0.5)" }}
                animate={{ scale: [1, 1.4, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: slot.delay + 1 }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
